import { createFileRoute } from "@tanstack/react-router";

import { BrandLockup, HeroSubtitle } from "@/components/marketing/BrandLockup";
import FaqSection from "@/components/marketing/FaqSection";
import FinalCta from "@/components/marketing/FinalCta";
import HowItWorks from "@/components/marketing/HowItWorks";
import { marketingHead } from "@/components/marketing/marketingHead";
import ProductHero, { ProductShot } from "@/components/marketing/ProductHero";
import RelatedProducts from "@/components/marketing/RelatedProducts";
import CatalogEquation, {
  AUDIO_FILE,
  FileIcon,
  LYRICS_FILE,
} from "@/components/marketing/sections/CatalogEquation";
import LanguageBand from "@/components/marketing/sections/LanguageBand";
import ReachSection from "@/components/marketing/sections/ReachSection";
import SavingsSection from "@/components/marketing/sections/SavingsSection";
import { SAVINGS_ICONS } from "@/components/marketing/sections/savingsIcons";
import Footer from "@/components/nav/Footer";

export const Route = createFileRoute("/lyrics")({
  component: LyricTranscriptionPage,
  head: () =>
    marketingHead({
      title: "AI Lyric Transcription | Lutely",
      description:
        "No line-by-line typing. No separate lyric file needed. Upload a track and get time-stamped lyrics in minutes.",
      path: "/lyrics",
    }),
});

const USES = [
  {
    title: "Track metadata",
    body: "Keep approved lyrics attached to the audio and ready for credits, delivery, and future releases.",
    icon: SAVINGS_ICONS.lyrics,
  },
  {
    title: "Clean lyric sheets",
    body: "Get both the unmasked lyrics and a clean lyric sheet with explicit words masked automatically.",
    icon: <path d="m5 5 14 14M8 4l12 12M5 12l7 7M4 18l2 2M15 4l5 5" />,
  },
  {
    title: "Lyric translation",
    body: "Translate the lyrics into new languages while keeping every version connected to the track.",
    icon: (
      <path d="M4 5h9M8 3v2M6 5c1 4 3 7 7 9M12 5c-1 4-3 7-7 9M14 19l3-8 3 8M15 16h4" />
    ),
  },
  {
    title: "Lyric videos",
    body: "Turn the same approved lyrics into visual content for social platforms and video channels.",
    icon: (
      <>
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="m10 9 5 3-5 3z" />
      </>
    ),
  },
  {
    title: "Sync & licensing",
    body: "Have accurate lyrics ready when music supervisors, partners, or licensees need them.",
    icon: (
      <path d="M8 12a4 4 0 0 1 4-4h3M16 12a4 4 0 0 1-4 4H9M14 5l3 3-3 3M10 19l-3-3 3-3" />
    ),
  },
  {
    title: "Fans & releases",
    body: "Publish lyrics with the music so listeners can follow every word in one connected experience.",
    icon: (
      <path d="M12 21s-8-4.7-8-11a4.5 4.5 0 0 1 8-2.8A4.5 4.5 0 0 1 20 10c0 6.3-8 11-8 11z" />
    ),
  },
];

const FAQ = [
  {
    question: "How accurate is the lyric transcription?",
    answer:
      "Lutely produces highly accurate lyric transcriptions. Accuracy can vary with vocal clarity, pronunciation, background vocals, and the mix, and you can review and edit anything if needed.",
  },
  {
    question: "Are the lyrics time-stamped?",
    answer:
      "Yes. Lyrics are time-stamped to the track and formatted for lyric videos and more.",
  },
  {
    question: "Do I get both explicit and clean lyrics?",
    answer:
      "Yes. When explicit lyrics are detected, Lutely gives you both the original lyrics and a clean lyric sheet with explicit words masked.",
  },
  {
    question: "Do I need to upload a separate lyric file?",
    answer:
      "Nope, never. Lutely automatically transcribes the lyrics directly from your track.",
  },
];

function LyricTranscriptionPage() {
  return (
    <main className="flex flex-col bg-white">
      <ProductHero
        title={
          <>
            <BrandLockup>Lyrics</BrandLockup>
            <br />
            <HeroSubtitle>Automatic lyric transcription.</HeroSubtitle>
          </>
        }
        lede={
          <>
            No line-by-line typing.
            <br />
            No separate lyric file needed.
            <br />
            Upload a track and get time-stamped lyrics in minutes.
          </>
        }
        cta="Transcribe Lyrics"
        proof={["60+ supported languages", "Free on every plan"]}
        stage={
          <ProductShot
            src="/marketing/lyrics-drawer.webp"
            alt="Lyrics open on a Lutely release page"
            caption="Real Lutely product screen"
          />
        }
      />
      <HowItWorks
        title={
          <>
            Having your lyrics is essential.
            <br />
            We make it <span className="text-blue-600">simple.</span>
          </>
        }
        intro="Upload a track and let Lutely transcribe the lyrics. Review every line, make edits, and use them everywhere your music goes."
        steps={[
          {
            title: "Upload",
            body: "Add a finished track and Lutely automatically starts transcribing the vocals into time-stamped, editable lyric sheets.",
          },
          {
            title: "Listen and Review",
            body: "Play the track beside the transcription and make any adjustments you want.",
          },
          {
            title: "Use Everywhere",
            body: "Keep the lyrics connected to CleanCut, translations, lyric videos, releases, and everything that comes next.",
          },
        ]}
      />
      <LanguageBand
        title={
          <>
            <span>60+</span> Languages
          </>
        }
        copy="Lutely transcribes lyrics in more than 60 languages, giving artists around the world the same automatic workflow."
      />
      <SavingsSection
        kicker="Time savings"
        title={
          <>
            Skip the typing.
            <br />
            <span>Keep creating.</span>
          </>
        }
        items={[
          { icon: SAVINGS_ICONS.lyrics, text: "Time-stamped automatically." },
          { icon: SAVINGS_ICONS.folder, text: "Formatted for DSPs." },
          {
            icon: <path d="M4 9v6M8 5v14M12 8v8M16 4v16M20 7v10" />,
            text: "Saved with your track forever.",
          },
        ]}
      />
      <ReachSection
        kicker="Why lyrics matter"
        title={
          <>
            One transcription.
            <br />
            <span>More ways to use it.</span>
          </>
        }
        intro="Accurate lyrics become the foundation for clean versions, translations, videos, releases, licensing, and fan experiences."
        items={USES}
      />
      <CatalogEquation
        kicker="Connected workflow"
        title="Transcribe once. Saved forever."
        body="Your time-stamped lyrics stay with the track in Lutely, ready whenever and wherever you need them."
        left={{ icons: <FileIcon>{AUDIO_FILE}</FileIcon>, label: "Upload" }}
        right={{
          icons: <FileIcon>{LYRICS_FILE}</FileIcon>,
          label: "Lyrics ready",
        }}
      />
      <FaqSection items={FAQ} />
      <RelatedProducts current="lyrics" />
      <FinalCta
        title="You make the music. Lutely handles what comes next."
        body="Prepare your tracks, build the release, and sell directly to fans from one connected platform."
      />
      <Footer topClassName="bg-[#050923]" />
    </main>
  );
}
